import './styles.scoped.css'
import {useEffect, useState} from 'react'
import {Typography, Button, Modal, TextField, MenuItem, Alert, Checkbox, ListItemText} from '@mui/material'
import {useGlobalStates} from '../../../../contexts/GlobalStatesContext'
import {adminApi} from '../../../../services'
import {parseError} from '../../../../utils/helpers'

// The branch created with the organization carries a branch code equal to its
// id; the backend treats it as the default, and so does this dialog.
const defaultBranchId = (organization, branches) =>
    (branches || []).find(branch => branch.branch_code === organization?.id)?.id || branches?.[0]?.id || ''

// Branches follow the role by default. An administrator runs the organization,
// so every branch; anyone else starts in the default one. Either can be
// changed — a site administrator is an administrator with one branch.
const branchesForRole = (role, organization, branches) => {
    if (role?.name === 'admin') return (branches || []).map(branch => branch.id)
    const fallback = defaultBranchId(organization, branches)
    return fallback ? [fallback] : []
}

export default ({open, setOpen, organization, roles, branches, onInvited}) => {
    const {setLoading, setMessageBox} = useGlobalStates()

    const [email, setEmail] = useState('')
    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [phone, setPhone] = useState('')
    const [roleId, setRoleId] = useState('')
    const [branchIds, setBranchIds] = useState([])
    // Once someone picks branches by hand, changing the role must not undo it.
    const [branchesTouched, setBranchesTouched] = useState(false)
    const [problem, setProblem] = useState(null)

    useEffect(() => {
        if (!open) return
        setEmail('')
        setFirstName('')
        setLastName('')
        setPhone('')
        // The first person invited to an agency is the one who runs it, so
        // Administrator is the sensible default when it is on offer.
        const initialRole = roles?.find(role => role.name === 'admin') || roles?.[0]
        setRoleId(initialRole?.id || '')
        // Every console screen is scoped to a user's branches, so someone with
        // none signs in to an empty console.
        setBranchIds(branchesForRole(initialRole, organization, branches))
        setBranchesTouched(false)
        setProblem(null)
    }, [open, roles, branches])

    const changeRole = id => {
        setRoleId(id)
        if (!branchesTouched) {
            setBranchIds(
                branchesForRole(
                    roles?.find(role => role.id === id),
                    organization,
                    branches,
                ),
            )
        }
    }

    // "All branches" is a promise about the future, not just the current list:
    // only an administrator in every branch is given branches opened later. A
    // scheduler in a one-branch agency is in every branch today, and should not
    // be told they will be in the next one.
    const isAdminRole = roles?.find(role => role.id === roleId)?.name === 'admin'
    const allBranches = isAdminRole && branches?.length > 0 && branchIds.length === branches.length

    const invite = () => {
        if (!email.trim() || !firstName.trim() || !lastName.trim()) {
            setProblem('Email, first name and last name are required.')
            return
        }
        if (!roleId) {
            setProblem('Choose a role. A user with none can sign in but see nothing.')
            return
        }
        if (!branchIds.length) {
            setProblem('Choose at least one branch. A user with none can sign in but see nothing.')
            return
        }
        // The default branch is where they land when they sign in, if they
        // belong to it; otherwise the first they were given.
        const fallback = defaultBranchId(organization, branches)
        const primaryBranchId = branchIds.includes(fallback) ? fallback : branchIds[0]
        setProblem(null)
        setLoading(true)
        adminApi
            .createOrgUser(organization?.id, {
                email: email.trim().toLowerCase(),
                first_name: firstName.trim(),
                last_name: lastName.trim(),
                phone: phone.trim(),
                role_id: roleId,
                branch_ids: branchIds,
                primary_branch_id: primaryBranchId,
                send_invite: true,
            })
            .then(({data}) => {
                setLoading(false)
                setOpen(false)
                // The API reports the user and the invitation separately: the
                // account can exist while the email did not go out, and saying
                // only "invited" would leave someone waiting for a message that
                // is never coming.
                const warning = data?.data?.warning
                setMessageBox(
                    warning
                        ? {status: 'warning', title: 'User added, but the invitation did not send', description: warning}
                        : {
                              status: 'success',
                              title: 'Invitation sent',
                              description: `${email.trim().toLowerCase()} can now set a password and sign in to ${organization?.name}.`,
                          },
                )
                onInvited?.()
            })
            .catch(error => {
                setLoading(false)
                const {title, description} = parseError(error)
                if (error?.response?.status === 409 || error?.response?.status === 400) {
                    setProblem(description || title)
                    return
                }
                setOpen(false)
                setMessageBox({
                    status: 'error',
                    title: title || 'Could not invite this user',
                    description: description || 'There was an error sending this invitation. Please try again later.',
                })
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    <div className="title">
                        <Typography variant="h6" color="text.primary">
                            Invite user
                        </Typography>
                        <br />
                        <Typography variant="body2" color="text.secondary">
                            They receive an email from {organization?.name} with a link to set their own password. Careviah never sets it for them.
                        </Typography>
                    </div>
                    <br />
                    {problem && (
                        <>
                            <Alert severity="error">{problem}</Alert>
                            <br />
                        </>
                    )}
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Email
                        </Typography>
                        <TextField placeholder="name@agency.com" fullWidth value={email} onChange={e => setEmail(e.target.value)} />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            First Name
                        </Typography>
                        <TextField placeholder="First name" fullWidth value={firstName} onChange={e => setFirstName(e.target.value)} />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Last Name
                        </Typography>
                        <TextField placeholder="Last name" fullWidth value={lastName} onChange={e => setLastName(e.target.value)} />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Phone Number
                        </Typography>
                        <TextField placeholder="Optional" fullWidth value={phone} onChange={e => setPhone(e.target.value)} />
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Role
                        </Typography>
                        <TextField select fullWidth value={roleId} onChange={e => changeRole(e.target.value)}>
                            {(roles || []).map(role => (
                                <MenuItem key={role.id} value={role.id}>
                                    {role.display_name || role.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">
                            What they can do once they sign in. Caregivers and clients are added elsewhere, so they are not listed here.
                        </Typography>
                    </div>
                    <div className="field">
                        <Typography variant="h8" color="text.primary">
                            Branches
                        </Typography>
                        <TextField
                            select
                            fullWidth
                            value={branchIds}
                            onChange={e => {
                                setBranchesTouched(true)
                                setBranchIds(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)
                            }}
                            slotProps={{
                                select: {
                                    multiple: true,
                                    renderValue: selected =>
                                        allBranches
                                            ? 'All branches'
                                            : (branches || [])
                                                  .filter(branch => selected.includes(branch.id))
                                                  .map(branch => branch.branch_name)
                                                  .join(', '),
                                },
                            }}
                        >
                            {(branches || []).map(branch => (
                                <MenuItem key={branch.id} value={branch.id}>
                                    <Checkbox checked={branchIds.includes(branch.id)} size="small" />
                                    <ListItemText primary={branch.branch_name} />
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography variant="body2" color="text.secondary">
                            {allBranches ? 'Every branch, including any added later.' : 'They only see the branches they belong to.'}
                        </Typography>
                    </div>
                    <div className="actions">
                        <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="contained" fullWidth onClick={invite}>
                            Send Invitation
                        </Button>
                    </div>
                </div>
            </div>
        </Modal>
    )
}
